import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiCheck, FiArrowRight, FiUsers, FiShield, 
  FiTool, FiClock, FiAlertCircle, FiLayers, FiMail, FiGlobe, 
  FiServer, FiSearch, FiChevronDown, FiBriefcase, FiActivity 
} from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { pageFaqs } from '../data/faqs';

const Support = () => {
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Support | ManagedSite.net";
    
    const description = "Get support from ManagedSite.net for website issues, technical requests and service questions.";
    const title = "Support | ManagedSite.net";
    
    const updateMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    
    updateMeta('meta[name="description"]', 'content', description);
    updateMeta('meta[property="og:title"]', 'content', title);
    updateMeta('meta[property="og:description"]', 'content', description);
    updateMeta('meta[property="twitter:title"]', 'content', title);
    updateMeta('meta[property="twitter:description"]', 'content', description);
  }, []);

  const faqs = pageFaqs['/support'];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="bg-[#FAF8F3] pt-24 pb-24 lg:pt-40 lg:pb-36 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-8">Support</div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-[#17211E] tracking-tight leading-[1.1] mb-10 max-w-4xl mx-auto">
            Get the Right Help Without the Runaround
          </h1>
          <p className="text-xl sm:text-2xl text-[#17211E]/80 leading-relaxed mb-12 max-w-3xl mx-auto font-medium">
            New inquiries start on the contact form. Once you are a client, you get a phone number and direct support desk access.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="home-button">
              Request Support
            </Link>
            <Link to="/services/technical-support" className="home-button-secondary">
              View Technical Support Services
            </Link>
          </div>
        </div>
      </section>

      {/* Choose Your Path */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#17211E] mb-16 text-center">How Should We Help?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { 
                title: "Support Request", 
                text: "Submit a technical issue, project request or partnership inquiry through the contact form.", 
                cta: "Request Support", 
                link: "/contact", 
                icon: FiMail,
                external: false
              },
              { 
                title: "Technical Support Services", 
                text: "Learn about technical support, prepaid support blocks, and how ongoing help works.", 
                cta: "View Technical Support", 
                link: "/services/technical-support", 
                icon: FiTool,
                external: false
              },
              { 
                title: "Business Support", 
                text: "Website, email, hosting and consulting help for businesses that want one person responsible for the setup.", 
                cta: "Tell Us What You Need", 
                link: "/contact", 
                icon: FiBriefcase,
                external: false
              }
            ].map((card, i) => (
              <div key={i} className="bg-[#FAF8F3] p-10 rounded-[2.5rem] border border-[#EAF1EC] shadow-sm flex flex-col h-full hover:shadow-md transition-all">
                <div className="bg-white w-14 h-14 rounded-2xl flex items-center justify-center text-[#174C3C] mb-8 shadow-sm">
                  <SafeIcon icon={card.icon} className="w-7 h-7" />
                </div>
                <h3 className="text-2xl font-extrabold text-[#17211E] mb-6">{card.title}</h3>
                <p className="text-lg text-[#17211E]/70 leading-relaxed font-medium mb-10 flex-grow">{card.text}</p>
                {card.external ? (
                  <a href={card.link} target="_blank" rel="noopener noreferrer" className="home-button w-full">
                    {card.cta}
                  </a>
                ) : (
                  <Link to={card.link} className="home-button w-full">
                    {card.cta}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Help Us Help You Faster */}
      <section className="py-24 bg-[#EAF1EC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8">Help Us Help You Faster</h2>
              <p className="text-xl text-[#17211E]/75 leading-relaxed font-medium mb-12">
                Clear requests get clearer responses. When you contact us, include as much of the following as you can.
              </p>
              <div className="space-y-4 mb-12">
                {[
                  "Your agency or company name",
                  "Website URL",
                  "What is happening",
                  "When it started",
                  "What changed recently",
                  "How urgent it is",
                  "Any relevant screenshots or error messages",
                  "Access notes if already approved under your account"
                ].map((item, i) => (
                  <div key={i} className="flex items-center text-xl font-bold text-[#17211E]">
                    <div className="w-7 h-7 bg-white rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                      <SafeIcon icon={FiCheck} className="w-4 h-4 text-[#174C3C]" />
                    </div>
                    {item}
                  </div>
                ))}
              </div>
              <Link to="/contact" className="home-button">
                Request Support
              </Link>
            </div>
            <div className="bg-white p-12 rounded-[3rem] border border-[#3B7C6D]/10 shadow-xl relative overflow-hidden">
              <div className="relative z-10">
                <div className="text-[#D7B77A] text-sm font-black uppercase tracking-[0.25em] mb-8">Triage Process</div>
                <div className="space-y-8">
                  {[
                    { t: "Submission", s: "Required details received" },
                    { t: "Review", s: "Technical environment check" },
                    { t: "Estimate", s: "Time and approach confirmed" },
                    { t: "Resolution", s: "Work completed and tested" }
                  ].map((step, i) => (
                    <div key={i} className="flex justify-between items-center pb-6 border-b border-[#EAF1EC] last:border-0 last:pb-0">
                      <span className="text-xl font-bold text-[#17211E]">{step.t}</span>
                      <span className="text-sm font-bold text-[#3B7C6D] bg-[#EAF1EC] px-4 py-1.5 rounded-full">{step.s}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-[#FAF8F3] rounded-full opacity-50"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Options for Agencies */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#17211E] mb-16 text-center">Support Options for Agencies</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { 
                title: "Included Plan Support", 
                text: "Some website management plans include limited technical support time. Unused included time does not roll over.", 
                link: "/agency-plans" 
              },
              { 
                title: "Hourly Agency Support", 
                text: "White-label agency technical support is available for work outside included plan time. The hourly rate is on the partner rate card.", 
                link: "/services/technical-support" 
              },
              { 
                title: "Prepaid Support Blocks", 
                text: "5-hour and 10-hour prepaid blocks. Valid for 12 months unless an agreement states otherwise.", 
                link: "/services/technical-support" 
              },
              { 
                title: "Emergency or After-Hours Support", 
                text: "When available, urgent work is billed at the emergency partner rate. Emergency or after-hours availability is not guaranteed.", 
                link: "/contact" 
              }
            ].map((option, i) => (
              <div key={i} className="bg-[#FAF8F3] p-10 rounded-3xl border border-[#EAF1EC] flex flex-col h-full">
                <h3 className="text-xl font-extrabold text-[#17211E] mb-6 leading-tight">{option.title}</h3>
                <p className="text-lg text-[#17211E]/70 font-medium leading-relaxed mb-8 flex-grow">{option.text}</p>
                <Link to={option.link} className="text-[#174C3C] font-bold hover:text-[#3B7C6D] flex items-center gap-2">
                  Learn More <SafeIcon icon={FiArrowRight} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Support Needs */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#17211E] mb-16 text-center">Common Reasons Agencies Contact Us</h2>
          <div className="flex flex-wrap justify-center gap-4">
            {[
              "Website down or partially broken", "Plugin or theme update issues", "Form or checkout problems",
              "DNS, domain or SSL issues", "Email delivery problems", "Hosting or performance issues",
              "Migration help", "Security concerns", "Content or configuration changes", "Help choosing the right plan"
            ].map((need, i) => (
              <div key={i} className="bg-white px-8 py-4 rounded-2xl border border-[#EAF1EC] font-bold text-[#17211E] shadow-sm">
                {need}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Existing Partners */}
      <section className="py-24 bg-white border-y border-[#EAF1EC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-[#17211E] mb-8">Already Working With ManagedSite.net?</h2>
          <p className="text-xl text-[#17211E]/75 mb-12 font-medium leading-relaxed">
            Use the phone number and support desk from your onboarding. This page is for people who are not clients yet.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/services/website-management" className="home-button">
              View Website Management
            </Link>
            <Link to="/contact" className="home-button-secondary">
              New inquiry
            </Link>
          </div>
        </div>
      </section>

      {/* Business Routing */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-[3rem] p-12 md:p-20 border border-[#EAF1EC] shadow-xl text-center">
            <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-8">For Businesses</div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#17211E] mb-8 leading-tight">Need Support for Your Own Business?</h2>
            <p className="text-xl text-[#17211E]/75 mb-12 font-medium leading-relaxed max-w-3xl mx-auto">
              Tell us what is happening, and we will help work out the next step.
            </p>
            <Link
              to="/contact"
              className="home-button"
            >
              Tell Us What You Need Help With
            </Link>
          </div>
        </div>
      </section>

      {/* Related Resources */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#17211E] mb-16">Related Resources</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { label: "Contact", link: "/contact" },
              { label: "Technical Support", link: "/services/technical-support" },
              { label: "Website Management", link: "/services/website-management" },
              { label: "Business Email", link: "/services/business-email" },
              { label: "Managed Hosting", link: "/services/managed-hosting" },
              { label: "Agency Plans", link: "/agency-plans" },
              { label: "About", link: "/about" }
            ].map((item, i) => (
              <Link key={i} to={item.link} className="bg-[#FAF8F3] p-8 rounded-2xl border border-[#EAF1EC] font-bold text-[#17211E] text-center hover:shadow-md transition-all">
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-24 bg-[#FAF8F3] border-t border-[#EAF1EC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#17211E] mb-16 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white border border-[#EAF1EC] rounded-2xl overflow-hidden shadow-sm">
                <button 
                  onClick={() => setOpenFaq(openFaq === i ? null : i)} 
                  aria-expanded={openFaq === i}
                  aria-controls={`faq-answer-${i}`}
                  className="w-full flex justify-between items-center p-8 text-left hover:bg-[#FAF8F3] transition-colors"
                >
                  <span className="text-xl font-bold text-[#17211E]">{faq.q}</span>
                  <SafeIcon icon={FiChevronDown} className={`w-6 h-6 text-[#3B7C6D] transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                <div 
                  id={`faq-answer-${i}`}
                  className={`px-8 pb-8 text-xl text-[#17211E]/75 font-medium leading-relaxed ${openFaq === i ? 'block' : 'hidden'}`}
                >
                  {faq.a}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-32 bg-[#174C3C] text-center relative overflow-hidden text-white">
        <div className="absolute top-0 left-0 w-64 h-64 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full translate-x-1/3 translate-y-1/3"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-10 tracking-tight leading-tight">
            Start With a Clear Support Request
          </h2>
          <p className="text-2xl text-[#FAF8F3]/80 mb-14 max-w-3xl mx-auto leading-relaxed font-medium">
            Tell us what is happening, which website is affected and how urgent it is. We will help from there.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact" className="home-button-on-dark">
              Request Support
            </Link>
            <Link to="/services/technical-support" className="home-button-on-dark-secondary">
              View Technical Support
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Support;